import {
  Autocomplete,
  Box,
  InputAdornment,
  TextField,
  createFilterOptions,
} from '@mui/material'
import Iconify from 'src/components/Iconify'
import { icons } from 'src/design-system'
import { vehicleLabel } from 'src/format'
import { VehiclesQuery } from 'src/generated'

type MapVehicle = VehiclesQuery['vehicles']['vehicles'][0]

type MapSearchProps = {
  vehicles: MapVehicle[]
  onSelect: (vehicle: MapVehicle) => void
}

// A find-a-vehicle box pinned to the map. Searching by name or plate flies the
// map to the match and opens its sheet. Only vehicles with a last-known
// location are offered (the rest can't be placed on the map).
//
// **The glyph is an `InputAdornment`, like every other search field.** It was a
// bare `Iconify` nudged with `ml: 0.5`, which is two drifts in one line: the
// nudge put it 4px further from the field's edge than the theme's own inset,
// and skipping the adornment took away the 8px the adornment holds between
// itself and the text, so the mark on the one search field that floats over a
// map sat 8px nearer the caret than the four that sit in toolbars. The ink is
// the adornment's too — it is the one thing in a field that is neither the
// value nor the label, and the theme already says what colour that is.
export const MapSearch = ({ vehicles, onSelect }: MapSearchProps) => {
  const options = vehicles.filter((vehicle) => vehicle.latestStatus)

  return (
    <Box sx={{ width: { xs: '100%', sm: 280 } }}>
      <Autocomplete
        size="small"
        options={options}
        blurOnSelect
        // The label is also the search key: MUI's default filter stringifies
        // an option through `getOptionLabel`. So the plate is kept in what is
        // matched against while being dropped from what is read, or a car
        // whose name carries a hyphenated plate stops being findable by
        // typing the hyphenated form. Searching by name or plate is this
        // control's whole job.
        filterOptions={createFilterOptions({
          stringify: (vehicle) => `${vehicle.name ?? ''} ${vehicle.reg ?? ''}`,
        })}
        getOptionLabel={(vehicle) => vehicleLabel(vehicle.name, vehicle.reg)}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        onChange={(_, value) => {
          if (value) {
            onSelect(value)
          }
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder="Find a vehicle"
            sx={{
              // Tint the rounded input itself (not the rectangular TextField
              // root) so no white corners bleed behind the pill on the map.
              '& .MuiOutlinedInput-root': {
                backgroundColor: 'background.paper',
                boxShadow: (theme) => theme.customShadows.z8,
              },
            }}
            InputProps={{
              ...params.InputProps,
              startAdornment: (
                <InputAdornment position="start">
                  <Iconify size="toolbar" icon={icons.search} />
                </InputAdornment>
              ),
            }}
          />
        )}
      />
    </Box>
  )
}
